import React from "react";
import { Col, Row, Dropdown, Button, Form } from "react-bootstrap";
import { FaChartBar, FaCube, FaMoneyBill, FaDollarSign } from "react-icons/fa";
import CountUp from "react-countup";
import Card from "../../../components/Card";
import { LABEL_KATEGORI, LABEL_ORDER_BY } from "../utils/chartConfig";
import { fmtDateInput } from "../hooks/useDashEkspor";

// ─── SummaryCard ──────────────────────────────────────────────────────────────

export const SummaryCard = ({ icon, bg, label, value, decimals }) => (
  <Col lg="3" md="6">
    <Card className="h-100 shadow-sm rounded-4">
      <Card.Body className="d-flex justify-content-between align-items-center">
        <div className={`${bg} text-white rounded-3 p-3 d-flex align-items-center justify-content-center`}>
          {icon}
        </div>
        <div className="d-flex flex-column align-items-end text-end" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-muted small">{label}</div>
          <h4
            className="mb-0 fw-bold text-nowrap"
            style={{ fontSize: "1.5rem" }}
            title={typeof value === "number" ? value.toLocaleString("id-ID") : "0"}
          >
            {typeof value === "number" && !isNaN(value) ? (
              <CountUp
                end={value}
                duration={2.5}
                decimals={decimals}
                formattingFn={(v) => v.toLocaleString("id-ID")}
              />
            ) : (
              <span>0</span>
            )}
          </h4>
        </div>
      </Card.Body>
    </Card>
  </Col>
);

// ─── SummaryRow ───────────────────────────────────────────────────────────────

export const SummaryRow = ({ jumFreq, jumVol, nilaiIDR, nilaiUSD }) => {
  const items = [
    { icon: <FaChartBar size={24} />, bg: "bg-info",    label: "Total Frekuensi",   value: jumFreq,           decimals: 0 },
    { icon: <FaCube size={24} />,     bg: "bg-warning", label: "Total Volume (Ton)", value: jumVol / 1000,    decimals: 2 },
    { icon: <FaMoneyBill size={24} />,bg: "bg-success", label: "Nilai IDR (Juta)",   value: nilaiIDR / 1e6,   decimals: 2 },
    { icon: <FaDollarSign size={24} />,bg: "bg-danger", label: "Nilai USD",           value: nilaiUSD,         decimals: 2 },
  ];
  return (
    <Row className="g-3 mb-4 mt-2">
      {items.map((item, i) => <SummaryCard key={i} {...item} />)}
    </Row>
  );
};

// ─── PeriodFilter ─────────────────────────────────────────────────────────────

export const PeriodFilter = ({
  start, end,
  tahuns,
  selectedTahun,
  onTahun, onCustomApply,
}) => {
  const [tempStart, setTempStart] = React.useState(fmtDateInput(start));
  const [tempEnd, setTempEnd]     = React.useState(fmtDateInput(end));
  const [labelTahun, setLabelTahun] = React.useState(selectedTahun ?? String(new Date().getFullYear()));

  const handleTahun = (val) => {
    const tahun = String(val);
    setLabelTahun(tahun);
    onTahun(tahun);
  };

  return (
    <Col lg={4} md={12}>
      <Form.Group className="form-group mb-3">
        <Form.Label className="text-white">Periode</Form.Label>
        <div className="d-flex gap-2 flex-nowrap">
          <Form.Control
            type="date" value={tempStart}
            onChange={(e) => setTempStart(e.target.value)}
            style={{ backgroundColor: "transparent", border: "1px solid #ccc", minWidth: 140, color: "white" }}
          />
          <Form.Control
            type="date" value={tempEnd}
            onChange={(e) => setTempEnd(e.target.value)}
            style={{ backgroundColor: "transparent", border: "1px solid #ccc", minWidth: 140, color: "white" }}
          />
          <Button variant="primary" onClick={() => onCustomApply(tempStart, tempEnd)}>
            Tampilkan
          </Button>
        </div>
      </Form.Group>

      <div className="d-flex gap-3 flex-wrap">
        <DropdownFilter
          label={`Tahun ${labelTahun}`}
          items={tahuns}
          getKey={(d) => String(d.year ?? d)}
          getLabel={(d) => String(d.year ?? d)}
          onSelect={handleTahun}
        />
      </div>
    </Col>
  );
};

// ─── DropdownFilter ───────────────────────────────────────────────────────────

export const DropdownFilter = ({ label, items = [], getKey, getLabel, onSelect, style }) => (
  <div className="d-flex align-items-center text-white">
    <Dropdown onSelect={onSelect}>
      <Dropdown.Toggle as={Button} variant="outline-light" size="sm" style={style}>
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {items.map((item) => (
          <Dropdown.Item key={getKey(item)} eventKey={getKey(item)}>
            {getLabel(item)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

// ─── CommonGroupingFilter ─────────────────────────────────────────────────────

export const CommonGroupingFilter = ({
  commonKategori, setCommonKategori,
  commonOrderBy, setCommonOrderBy,
  commonLimit, setCommonLimit,
}) => (
  <Row className="mb-3">
    <Col>
      <DropdownFilter
        label={`Kategori : ${LABEL_KATEGORI[commonKategori]}`}
        items={Object.entries(LABEL_KATEGORI)}
        getKey={([k]) => k}
        getLabel={([, v]) => v}
        onSelect={setCommonKategori}
        style={{ backgroundColor: "transparent", border: "1px solid #ccc", minWidth: 140, color: "black" }}
      />
    </Col>
    <Col>
      <DropdownFilter
        label={`Berdasarkan : ${LABEL_ORDER_BY[commonOrderBy]}`}
        items={Object.entries(LABEL_ORDER_BY)}
        getKey={([k]) => k}
        getLabel={([, v]) => v}
        onSelect={setCommonOrderBy}
        style={{ backgroundColor: "transparent", border: "1px solid #ccc", minWidth: 140, color: "black" }}
      />
    </Col>
    <Col>
      <DropdownFilter
        label={`Limit : ${commonLimit === "all" ? "SEMUA" : `TOP ${commonLimit}`}`}
        items={[["all", "Semua"], ...[5, 7, 10].map((n) => [n, `TOP ${n}`])]}
        getKey={([k]) => k}
        getLabel={([, v]) => v}
        onSelect={(val) => setCommonLimit(val === "all" ? "all" : Number(val))}
        style={{ backgroundColor: "transparent", border: "1px solid #ccc", minWidth: 140, color: "black" }}
      />
    </Col>
  </Row>
);

// ─── ChartCard ────────────────────────────────────────────────────────────────

export const ChartCard = ({ title, header, children }) => (
  <Col md="12">
    <div className="card">
      <div className="flex-wrap card-header d-flex justify-content-between">
        <div className="header-title">
          {title && <h5 className="card-title">{title}</h5>}
          {header}
        </div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  </Col>
);
